import { useLayoutEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { DEFAULT_NAMESPACE } from '@backstage/catalog-model';
import type { DependencyGraphTypes } from '@backstage/core-components';
import type { EntityNode } from '@backstage/plugin-catalog-graph';
import { useEntityPresentation } from '@backstage/plugin-catalog-react';
import { getNodeCategory, NODE_CATEGORY_COLORS } from './nodeCategory';

const cx = (...classes: Array<string | false | undefined>) =>
  classes.filter(Boolean).join(' ');

const useStyles = makeStyles(
  theme => {
    const mode = theme.palette.type === 'dark' ? 'dark' : 'light';
    const categoryFill = (color: { light: string; dark: string }) => ({
      fill: color[mode],
      stroke: color[mode],
    });

    return {
      node: {
        fill: theme.palette.grey[300],
        stroke: theme.palette.grey[300],
        '&.primary': {
          fill: theme.palette.primary.light,
          stroke: theme.palette.primary.light,
        },
        '&.secondary': {
          fill: theme.palette.secondary.light,
          stroke: theme.palette.secondary.light,
        },
        '&.backend': categoryFill(NODE_CATEGORY_COLORS.backend),
        '&.frontend': categoryFill(NODE_CATEGORY_COLORS.frontend),
        '&.resource': categoryFill(NODE_CATEGORY_COLORS.resource),
      },
      text: {
        fill: theme.palette.getContrastText(theme.palette.grey[300]),
        '&.primary': {
          fill: theme.palette.primary.contrastText,
        },
        '&.secondary': {
          fill: theme.palette.secondary.contrastText,
        },
        '&.backend': {
          fill: theme.palette.getContrastText(NODE_CATEGORY_COLORS.backend[mode]),
        },
        '&.frontend': {
          fill: theme.palette.getContrastText(NODE_CATEGORY_COLORS.frontend[mode]),
        },
        '&.resource': {
          fill: theme.palette.getContrastText(NODE_CATEGORY_COLORS.resource[mode]),
        },
        '&.focused': {
          fontWeight: 'bold',
        },
      },
      clickable: {
        cursor: 'pointer',
      },
    };
  },
  { name: 'CategoryColorNode' },
);

/**
 * Catalog graph renderNode that colors nodes by entity category
 * (backend/frontend/resource) instead of the library's uniform default.
 *
 * The root/focused entity keeps the library's own "secondary" highlight
 * (the "you are here" cue) instead of its category color.
 */
export function CategoryColorNode({
  node,
}: DependencyGraphTypes.RenderNodeProps<EntityNode>) {
  const { id, entity, color = 'default', focused, onClick } = node;
  const classes = useStyles();
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const textRef = useRef<SVGTextElement | null>(null);

  const presentation = useEntityPresentation(entity, {
    defaultNamespace: DEFAULT_NAMESPACE,
  });

  useLayoutEffect(() => {
    if (textRef.current) {
      let { height: renderedHeight, width: renderedWidth } =
        textRef.current.getBBox();
      renderedHeight = Math.round(renderedHeight);
      renderedWidth = Math.round(renderedWidth);
      if (renderedHeight !== height || renderedWidth !== width) {
        setHeight(renderedHeight);
        setWidth(renderedWidth);
      }
    }
  }, [width, height]);

  const category = getNodeCategory(entity);
  const colorClass =
    color === 'secondary'
      ? 'secondary'
      : category ?? (color === 'primary' ? 'primary' : undefined);

  const padding = 10;
  const paddedWidth = width + padding * 2;
  const paddedHeight = height + padding * 2;
  const displayTitle = presentation.primaryTitle ?? id;

  return (
    <g onClick={onClick} className={cx(onClick && classes.clickable)}>
      <rect
        className={cx(classes.node, colorClass)}
        width={paddedWidth}
        height={paddedHeight}
        rx={10}
      />
      <text
        ref={textRef}
        className={cx(classes.text, colorClass, focused && 'focused')}
        y={paddedHeight / 2}
        x={paddedWidth / 2}
        textAnchor="middle"
        alignmentBaseline="middle"
      >
        {displayTitle}
      </text>
      <title>{presentation.entityRef}</title>
    </g>
  );
}
